import { useState, useCallback } from 'react';

export interface DetailViewState<T> {
    item: T | null;
    setItem: React.Dispatch<React.SetStateAction<T | null>>;
    isEditing: boolean;
    setIsEditing: (v: boolean) => void;
    isNew: boolean;
    setIsNew: (v: boolean) => void;
    loading: boolean;
    setLoading: (v: boolean) => void;
    saving: boolean;
    error: string | null;
    setError: (v: string | null) => void;
    startEdit: () => void;
    cancelEdit: () => void;
    update: <K extends keyof T>(field: K, value: T[K]) => void;
    withLoading: (fn: () => Promise<void>) => Promise<void>;
    withSaving: (fn: () => Promise<void>) => Promise<void>;
}

export function useDetailView<T>(): DetailViewState<T> {
    const [item, setItem] = useState<T | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isNew, setIsNew] = useState(false);
    const [copy, setCopy] = useState<T | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const startEdit = useCallback(() => {
        setCopy(JSON.parse(JSON.stringify(item)));
        setIsEditing(true);
    }, [item]);

    const cancelEdit = useCallback(() => {
        if (copy) setItem(copy);
        setIsEditing(false);
    }, [copy]);

    const update = useCallback(<K extends keyof T>(field: K, value: T[K]) => {
        setItem(prev => prev ? { ...prev, [field]: value } : prev);
    }, []);

    const withLoading = useCallback(async (fn: () => Promise<void>) => {
        setLoading(true);
        setError(null);
        try {
            await fn();
        } catch (err) {
            setError(String(err));
        } finally {
            setLoading(false);
        }
    }, []);

    const withSaving = useCallback(async (fn: () => Promise<void>) => {
        setSaving(true);
        setError(null);
        try {
            await fn();
        } catch (err) {
            setError(String(err));
        } finally {
            setSaving(false);
        }
    }, []);

    return {
        item, setItem,
        isEditing, setIsEditing,
        isNew, setIsNew,
        loading, setLoading,
        saving,
        error, setError,
        startEdit, cancelEdit,
        update,
        withLoading, withSaving,
    };
}
